import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFileNavigationContext } from '../contexts/FileNavigationContext';

const useChangeNavigationOnActiveFile = () => {
  const navigate = useNavigate();
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const { selectFile } = useFileNavigationContext();

  const changeNavigationOnActiveFile = useCallback(
    ({
      filePath,
      fileParent,
      metaInfo,
      children,
      isApiFile = false,
      workspaceId = activeWorkspace?.slug,
      inEditMode = false,
    }) => {
      if (!workspaceId) {
        console.error('No workspace ID found');
        return;
      }

      const basePath = `/spaces/${workspaceId}`;
      const editSuffix = inEditMode ? '/edit' : '';

      // Handle API file navigation
      if (isApiFile) {
        if (typeof filePath === 'string' && filePath?.includes('/')) {
          filePath = filePath.split('/')[0];
        }
        navigate(`${basePath}/pages/${filePath}${editSuffix}`);
        return;
      }

      // Update the shared navigation state
      if (filePath && filePath !== 'workspace') {
        selectFile(filePath, fileParent, isApiFile);
      }

      if (filePath === 'folder-page') {
        navigate(`${basePath}/folder/${fileParent}`);
        return;
      }

      // Handle folder navigation
      if (filePath === 'folder') {
        const defaultFile = metaInfo?.default_file_ref_id || children?.[0]?.ref_id || children?.[0]?.id || '';
        navigate(`${basePath}/pages/${defaultFile}${editSuffix}`);
        return;
      }

      // Handle workspace navigation
      if (filePath === 'workspace') {
        navigate(basePath);
        return;
      }

      // Handle regular file navigation
      if (typeof filePath === 'string' && filePath?.includes('/')) {
        filePath = filePath.split('/')[0];
      }
      navigate(`${basePath}/pages/${filePath}${editSuffix}`);
    },
    [navigate, selectFile, activeWorkspace]
  );

  // New function to handle API method navigation with query parameters
  const navigateToApiMethod = useCallback(
    ({ apiData, methodData, workspace }) => {
      const workspaceId = workspace ?? apiData?.slug ?? activeWorkspace?.slug;
      if (!workspaceId || !apiData?.ref_id || !methodData) {
        console.error('Missing required data for API method navigation');
        return;
      }
      const basePath = `/spaces/${workspaceId}`;
      const filePathSegment = `pages/${apiData.ref_id}`;

      // Create URL with query parameters for method, path, and tag
      const searchParams = new URLSearchParams();
      if (methodData.method) searchParams.set('method', methodData.method);
      if (methodData.path) searchParams.set('path', methodData.path);
      if (methodData.tags?.[0]) searchParams.set('tag', methodData.tags[0]);
      else searchParams.set('tag', 'default');

      const queryString = searchParams.toString();
      const fullUrl = `${basePath}/${filePathSegment}${queryString ? `?${queryString}` : ''}`;

      // Navigate to the URL with query parameters
      // window.location.href = fullUrl;
      navigate(fullUrl);
    },
    [activeWorkspace]
  );

  return {
    changeNavigationOnActiveFile,
    navigateToApiMethod,
  };
};

export default useChangeNavigationOnActiveFile;
